/**
 * ShoppingCartModal Component
 * Multi-step modal for placing form orders:
 *   Step 1: Review cart items
 *   Step 2: Shipping address (auto-loaded user address or manual entry)
 *   Step 3: Confirm & submit order
 */

import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  ShoppingCartIcon,
  TrashIcon,
  MapPinIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSelect, OathInput, OathDualSpinner, OathAlert } from '../../shared/OathUI';
import { getUserAddress, submitFormOrder } from '../../../services/oathFormsService';
import { toast } from 'react-toastify';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC',
];

const EMPTY_MANUAL_ADDRESS = {
  firstName: '',
  lastName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
};

/**
 * Step indicator
 */
const StepIndicator = ({ currentStep }) => {
  const steps = ['Review Cart', 'Shipping', 'Confirm'];
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {steps.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = stepNum === currentStep;
        const isCompleted = stepNum < currentStep;
        return (
          <div key={label} className="flex items-center gap-2">
            {idx > 0 && (
              <div className={`w-8 h-px ${isCompleted ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`} />
            )}
            <div className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                isActive
                  ? 'bg-primary text-white'
                  : isCompleted
                  ? 'bg-primary/20 text-primary dark:text-primary-light'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
                {isCompleted ? (
                  <CheckCircleIcon className="h-4 w-4" />
                ) : (
                  stepNum
                )}
              </div>
              <span className={`text-xs font-medium hidden sm:inline ${
                isActive ? 'text-primary dark:text-primary-light' : 'text-gray-500 dark:text-gray-400'
              }`}>
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * ShoppingCartModal
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close handler
 * @param {Map} cart - Map<formId, quantity>
 * @param {Array} forms - All forms array (both state + bankers, to look up names)
 * @param {Function} onRemoveItem - (formId) callback to remove from cart
 * @param {Function} onClearCart - Callback to clear entire cart
 * @param {number} agencyId - Agency ID for address lookup and order submission
 */
const ShoppingCartModal = ({
  isOpen,
  onClose,
  cart,
  forms,
  onRemoveItem,
  onClearCart,
  agencyId,
}) => {
  const [step, setStep] = useState(1);
  const [addressMode, setAddressMode] = useState('my-address');
  const [userAddress, setUserAddress] = useState(null);
  const [loadingUserAddress, setLoadingUserAddress] = useState(false);
  const [userAddressError, setUserAddressError] = useState(null);
  const [manualAddress, setManualAddress] = useState(EMPTY_MANUAL_ADDRESS);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);

  // Build cart items with form metadata
  const cartItems = [];
  if (cart && forms) {
    cart.forEach((qty, formId) => {
      if (qty > 0) {
        const form = forms.find((f) => f.formId === formId);
        if (form) {
          cartItems.push({ ...form, quantity: qty });
        }
      }
    });
  }

  // Compute the resolved shipping address based on current mode
  const shippingAddress = useMemo(() => {
    if (addressMode === 'my-address') {
      return userAddress;
    }
    // For "other" mode, return manualAddress only if required fields are filled
    const { firstName, lastName, addressLine1, city, state, zipCode } = manualAddress;
    if (firstName && lastName && addressLine1 && city && state && zipCode) {
      return manualAddress;
    }
    return null;
  }, [addressMode, userAddress, manualAddress]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setAddressMode('my-address');
      setManualAddress(EMPTY_MANUAL_ADDRESS);
      setFormErrors({});
      setOrderSuccess(false);
    }
  }, [isOpen]);

  // Load user's address when moving to step 2
  const goToStep2 = useCallback(async () => {
    // If we already fetched, skip re-fetch
    if (userAddress) {
      setStep(2);
      return;
    }

    setLoadingUserAddress(true);
    setUserAddressError(null);
    const result = await getUserAddress(agencyId);
    setLoadingUserAddress(false);

    if (result.success) {
      setUserAddress(result.data);
    } else {
      setUserAddressError(result.error || 'Could not load your address');
    }
    // Always advance to step 2 — user can manually enter if fetch failed
    setStep(2);
  }, [agencyId, userAddress]);

  // Validate manual address fields
  const validateManualAddress = useCallback(() => {
    const errors = {};
    const required = ['firstName', 'lastName', 'addressLine1', 'city', 'state', 'zipCode'];
    required.forEach((field) => {
      if (!manualAddress[field]?.trim()) {
        const label = field === 'addressLine1' ? 'Address' : field.replace(/([A-Z])/g, ' $1').trim();
        errors[field] = `${label.charAt(0).toUpperCase() + label.slice(1)} is required`;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [manualAddress]);

  // Handle advancing from step 2 to step 3
  const goToStep3 = useCallback(() => {
    if (addressMode === 'other') {
      if (!validateManualAddress()) return;
    }
    setStep(3);
  }, [addressMode, validateManualAddress]);

  // Submit order
  const handleSubmitOrder = useCallback(async () => {
    if (!shippingAddress || cartItems.length === 0) return;

    setIsSubmitting(true);

    const orderPayload = {
      agencyId,
      orderItems: cartItems.map((item) => ({
        formId: item.formId,
        quantity: item.quantity,
        formName: item.formName,
      })),
      shippingAddress: {
        firstName: shippingAddress.firstName,
        lastName: shippingAddress.lastName,
        addressLine1: shippingAddress.addressLine1,
        addressLine2: shippingAddress.addressLine2 || '',
        city: shippingAddress.city,
        state: shippingAddress.state,
        zipCode: shippingAddress.zipCode,
      },
    };

    const result = await submitFormOrder(orderPayload);
    setIsSubmitting(false);

    if (result.success) {
      setOrderSuccess(true);
      toast.success('Order placed successfully!');
      onClearCart();
    } else {
      toast.error(result.error || 'Failed to place order');
    }
  }, [shippingAddress, cartItems, agencyId, onClearCart]);

  // Update a manual address field
  const updateManualField = (field, value) => {
    setManualAddress((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const stateOptions = US_STATES.map((s) => ({ value: s, label: s }));

  const renderFooter = () => {
    if (orderSuccess) {
      return (
        <OathButton onClick={onClose} className="ml-auto">
          Done
        </OathButton>
      );
    }

    switch (step) {
      case 1:
        return (
          <>
            <OathButton variant="outline" onClick={onClose}>
              Cancel
            </OathButton>
            <OathButton
              onClick={goToStep2}
              disabled={cartItems.length === 0}
              loading={loadingUserAddress}
              icon={ChevronRightIcon}
              className="ml-auto"
            >
              Select Address
            </OathButton>
          </>
        );
      case 2:
        return (
          <>
            <OathButton variant="outline" onClick={() => setStep(1)} icon={ChevronLeftIcon}>
              Back
            </OathButton>
            <OathButton
              onClick={goToStep3}
              disabled={addressMode === 'my-address' ? !userAddress : false}
              icon={ChevronRightIcon}
              className="ml-auto"
            >
              Review Order
            </OathButton>
          </>
        );
      case 3:
        return (
          <>
            <OathButton variant="outline" onClick={() => setStep(2)} icon={ChevronLeftIcon}>
              Back
            </OathButton>
            <OathButton
              onClick={handleSubmitOrder}
              loading={isSubmitting}
              icon={CheckCircleIcon}
              className="ml-auto"
            >
              Confirm & Send Order
            </OathButton>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title="Place an Order"
      size="lg"
      footer={renderFooter()}
    >
      {/* Order success state */}
      {orderSuccess ? (
        <div className="text-center py-8">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Order Placed Successfully
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Your form order has been sent to Bankers Surety. You will receive a confirmation email shortly.
          </p>
        </div>
      ) : (
        <>
          <StepIndicator currentStep={step} />

          {/* Step 1: Review Cart */}
          {step === 1 && (
            <div>
              {cartItems.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Your cart is empty. Add quantities to orderable forms to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cartItems.map((item) => (
                    <div
                      key={item.formId}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {item.formName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {item.state || 'ALL'} {item.unitSize ? `| ${item.unitSize} per unit` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-sm font-semibold text-primary dark:text-primary-light">
                          x{item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => onRemoveItem(item.formId)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Remove from cart"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <p className="text-sm text-gray-500 dark:text-gray-400 pt-2">
                    {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} in cart
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Shipping Address */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Address mode toggle */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAddressMode('my-address')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    addressMode === 'my-address'
                      ? 'bg-primary text-white shadow-lg shadow-primary/30'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <MapPinIcon className="h-4 w-4" />
                  My Address
                </button>
                <button
                  type="button"
                  onClick={() => setAddressMode('other')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    addressMode === 'other'
                      ? 'bg-primary text-white shadow-lg shadow-primary/30'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  Other Address
                </button>
              </div>

              {/* My Address mode */}
              {addressMode === 'my-address' && (
                <>
                  {loadingUserAddress && (
                    <div className="flex items-center justify-center py-6">
                      <OathDualSpinner size="sm" />
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading your address...</span>
                    </div>
                  )}

                  {userAddressError && !loadingUserAddress && (
                    <OathAlert
                      variant="warning"
                      message={`${userAddressError}. You can enter an address manually using "Other Address".`}
                    />
                  )}

                  {userAddress && !loadingUserAddress && (
                    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPinIcon className="h-4 w-4 text-primary dark:text-primary-light" />
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Shipping Address
                        </h4>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 pl-6">
                        <p className="font-medium">{userAddress.firstName} {userAddress.lastName}</p>
                        <p>{userAddress.addressLine1}</p>
                        {userAddress.addressLine2 && (
                          <p>{userAddress.addressLine2}</p>
                        )}
                        <p>{userAddress.city}, {userAddress.state} {userAddress.zipCode}</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Other Address mode — manual entry form */}
              {addressMode === 'other' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <OathInput
                      label="First Name"
                      value={manualAddress.firstName}
                      onChange={(val) => updateManualField('firstName', val)}
                      error={formErrors.firstName}
                      maxLength={50}
                      required
                    />
                    <OathInput
                      label="Last Name"
                      value={manualAddress.lastName}
                      onChange={(val) => updateManualField('lastName', val)}
                      error={formErrors.lastName}
                      maxLength={50}
                      required
                    />
                  </div>
                  <OathInput
                    label="Address Line 1"
                    value={manualAddress.addressLine1}
                    onChange={(val) => updateManualField('addressLine1', val)}
                    error={formErrors.addressLine1}
                    maxLength={80}
                    required
                  />
                  <OathInput
                    label="Address Line 2"
                    value={manualAddress.addressLine2}
                    onChange={(val) => updateManualField('addressLine2', val)}
                    placeholder="Apt, Suite, etc. (optional)"
                    maxLength={80}
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <OathInput
                      label="City"
                      value={manualAddress.city}
                      onChange={(val) => updateManualField('city', val)}
                      error={formErrors.city}
                      maxLength={25}
                      required
                    />
                    <OathSelect
                      label="State"
                      value={manualAddress.state}
                      onChange={(val) => updateManualField('state', val)}
                      options={stateOptions}
                      placeholder="State"
                      error={formErrors.state}
                      required
                    />
                    <OathInput
                      label="Zip Code"
                      value={manualAddress.zipCode}
                      onChange={(val) => updateManualField('zipCode', val)}
                      error={formErrors.zipCode}
                      maxLength={5}
                      required
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirm Order */}
          {step === 3 && (
            <div className="space-y-5">
              <OathAlert
                variant="info"
                message="Please review your order below. Once confirmed, it will be sent to Bankers Surety for processing."
              />

              {/* Items summary */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Order Items
                </h4>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50">
                        <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-2">
                          Form
                        </th>
                        <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-2 w-16">
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {cartItems.map((item) => (
                        <tr key={item.formId}>
                          <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-white">
                            {item.formName}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300 text-right font-medium">
                            {item.quantity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Address summary */}
              {shippingAddress && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Shipping To
                  </h4>
                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300">
                    <p className="font-medium">{shippingAddress.firstName} {shippingAddress.lastName}</p>
                    <p>{shippingAddress.addressLine1}{shippingAddress.addressLine2 ? `, ${shippingAddress.addressLine2}` : ''}</p>
                    <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zipCode}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </OathModal>
  );
};

export default memo(ShoppingCartModal);
